import React, { useState } from 'react';
import { Play } from 'lucide-react';
import SearchableTicker from '../../SearchableTicker';
import ExpirationDateSelect from '../../Watchlist/ExpirationDateSelect';
import StrikePriceSelect from '../../Watchlist/StrikePriceSelect';
import AccordionFormSection from '../../Screener/AccordionFormSection';
import IntermediatePresetCard from '../../Screener/IntermediatePresetCard';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { actionButtonTemplate } from '../../../theme';
import {
  SPREAD_TYPES,
  spreadSpeedPresets,
  deriveSpreadType,
  isContractRowComplete,
  buildContractSpreadPayload,
} from '../../../utils/spreadMappings';

const EMPTY_ROW = {
  ticker: '',
  option_type: 'call',
  expiration: '',
  long_strike: '',
  short_strike: '',
};

/**
 * Exact-contract entry for the Spread Simulator.
 *
 * The counterpart to SpreadForm, which describes geometry to scan. Here the
 * user names the expiration and both strikes — the question you actually have
 * in front of an order ticket — and the strategy is DERIVED from which strike
 * they are buying rather than asked for separately. That makes an inconsistent
 * entry (a "call debit spread" whose long strike sits above its short) simply
 * unrepresentable.
 *
 * Row mechanics follow WatchlistParamsForm, including its cascading clears:
 * changing something upstream invalidates the choices that depended on it, so
 * a stale strike can never survive a ticker change.
 */
function SpreadContractForm({ onRun, loading, canAppend, onRunAppend }) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [speedKey, setSpeedKey] = useState('balanced');
  const [speedExpanded, setSpeedExpanded] = useState(false);

  // --- Row edits, with dependent fields cleared ---
  const handleRowChange = (index, updates) => {
    setRows((prev) => {
      const next = [...prev];
      const row = next[index];

      // Ticker → a different underlying has its own expirations and strikes.
      if (updates.ticker !== undefined && updates.ticker !== row.ticker) {
        next[index] = { ...row, ...updates, expiration: '', long_strike: '', short_strike: '' };
      // Option type → the expiration chain is shared, but strikes are per-type.
      } else if (updates.option_type !== undefined && updates.option_type !== row.option_type) {
        next[index] = { ...row, ...updates, long_strike: '', short_strike: '' };
      // Expiration → strikes are listed per expiration.
      } else if (updates.expiration !== undefined && updates.expiration !== row.expiration) {
        next[index] = { ...row, ...updates, long_strike: '', short_strike: '' };
      } else {
        next[index] = { ...row, ...updates };
      }
      return next;
    });
  };

  const handleAddRow = () => setRows((prev) => [...prev, { ...EMPTY_ROW }]);

  const handleRemoveRow = (index) =>
    setRows((prev) => prev.filter((_, i) => i !== index));

  const handleDuplicateRow = (index) =>
    setRows((prev) => [
      ...prev.slice(0, index + 1),
      { ...prev[index] },
      ...prev.slice(index + 1),
    ]);

  const completeCount = rows.filter(isContractRowComplete).length;
  const isFormValid = completeCount > 0;

  const submit = (handler) => {
    const payload = buildContractSpreadPayload(rows, speedKey);
    if (payload && handler) handler(payload);
  };

  const getDisabledMessage = () => {
    const row = rows.find((r) => !isContractRowComplete(r)) || rows[0];
    if (!row?.ticker) return 'Please select a ticker';
    if (!row.expiration) return 'Please select an expiration';
    if (!row.long_strike || !row.short_strike) return 'Please select both strikes';
    if (row.long_strike === row.short_strike) return 'Both legs are at the same strike';
    return 'Please complete a spread';
  };

  return (
    <div className="spread-form space-y-4">
      {rows.map((row, index) => {
        const spreadKey = deriveSpreadType(row.option_type, row.long_strike, row.short_strike);
        const config = spreadKey ? SPREAD_TYPES[spreadKey] : null;
        const sameStrike = row.long_strike && row.short_strike
          && String(row.long_strike) === String(row.short_strike);

        return (
          <div key={index} className="card space-y-3" data-tour="spread-contract-row">
            <div className="flex justify-between items-center">
              <span className="font-bold text-brand-blue">Spread {index + 1}</span>
              <div className="flex gap-2">
                <button type="button" onClick={() => handleDuplicateRow(index)} className="btn-ghost-primary">
                  Duplicate
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveRow(index)}
                  className="btn-ghost-danger"
                  disabled={rows.length <= 1}
                  style={rows.length <= 1 ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                >
                  Remove
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="filter-heading">TICKER</label>
                <SearchableTicker
                  value={row.ticker ? { label: row.ticker, value: row.ticker } : null}
                  onChange={(selected) => handleRowChange(index, { ticker: selected?.value || '' })}
                />
              </div>

              <div>
                <label className="filter-heading">OPTION TYPE</label>
                <select
                  className="input"
                  value={row.option_type}
                  onChange={(e) => handleRowChange(index, { option_type: e.target.value })}
                >
                  <option value="call">Calls</option>
                  <option value="put">Puts</option>
                </select>
                <div className="h-5 mt-1" />
              </div>

              <div className="lg:col-span-2">
                <label className="filter-heading">
                  EXPIRATION
                  <span className="text-xs text-gray-400 ml-2">(both legs share it)</span>
                </label>
                <ExpirationDateSelect
                  ticker={row.ticker}
                  value={row.expiration}
                  onChange={(expiration) => handleRowChange(index, { expiration })}
                  disabled={false}
                />
              </div>

              <div className="lg:col-span-2">
                <label className="filter-heading">
                  LONG STRIKE
                  <span className="text-xs text-gray-400 ml-2">(the leg you buy)</span>
                </label>
                <StrikePriceSelect
                  ticker={row.ticker}
                  optionType={row.option_type}
                  expiration={row.expiration}
                  value={row.long_strike}
                  onChange={(long_strike) => handleRowChange(index, { long_strike })}
                  disabled={false}
                />
              </div>

              <div className="lg:col-span-2">
                <label className="filter-heading">
                  SHORT STRIKE
                  <span className="text-xs text-gray-400 ml-2">(the leg you sell)</span>
                </label>
                <StrikePriceSelect
                  ticker={row.ticker}
                  optionType={row.option_type}
                  expiration={row.expiration}
                  value={row.short_strike}
                  onChange={(short_strike) => handleRowChange(index, { short_strike })}
                  disabled={false}
                />
              </div>
            </div>

            {/* The derived strategy. Which vertical this is falls out of the
                two strikes, so it is shown rather than asked. */}
            {config && (
              <div
                className="flex flex-wrap items-center gap-2 text-sm rounded px-3 py-2"
                style={{
                  background: config.isCredit ? '#10b98115' : '#218aff15',
                  border: `1px solid ${config.isCredit ? '#10b981' : '#218aff'}40`,
                }}
              >
                <span className="font-semibold text-white">{config.label}</span>
                <span className="text-gray-400">·</span>
                <span style={{ color: config.isCredit ? '#10b981' : '#218aff' }}>
                  {config.direction}
                </span>
                <span className="text-gray-400">·</span>
                <span className="text-gray-400">{config.riskNote}</span>
              </div>
            )}
            {sameStrike && (
              <p className="text-sm text-red-400">
                Both legs are at the same strike — a vertical spread needs two different strikes.
              </p>
            )}
          </div>
        );
      })}

      {/* Exit timing, shared by every row — the same section SpreadForm ends
          with, so the setting means the same thing in both entry modes. */}
      <AccordionFormSection
        title="Exit Timing"
        icon=""
        summary={spreadSpeedPresets[speedKey]?.label || ''}
        isExpanded={speedExpanded}
        isDisabled={false}
        isCompleted={false}
        onToggle={() => setSpeedExpanded((v) => !v)}
        isMobile={isMobile}
        dataTour="spread-contract-speed"
      >
        <p className="section-helper-text">
          When you plan to close. You can change this after the run with the
          Days-to-Exit slider.
        </p>
        <div className="intermediate-preset-grid">
          {Object.entries(spreadSpeedPresets).map(([key, preset]) => (
            <IntermediatePresetCard
              key={key}
              presetKey={key}
              label={preset.label}
              description={preset.description}
              tooltip={preset.tooltip}
              isSelected={speedKey === key}
              onClick={() => setSpeedKey(key)}
            />
          ))}
        </div>
      </AccordionFormSection>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={() => submit(onRun)}
          disabled={!isFormValid || loading}
          className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.runSolid} flex-1 ${!isFormValid ? actionButtonTemplate.cssClasses.disabled : ''}`}
          style={{ position: 'relative' }}
        >
          {loading ? (
            <span className="spinner" />
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Play size={14} style={{ fill: 'currentColor' }} />
              {completeCount > 1 ? `Price ${completeCount} Spreads` : 'Price Spread'}
            </span>
          )}
          {!isFormValid && (
            <span className={actionButtonTemplate.cssClasses.tooltip}>
              {getDisabledMessage()}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={handleAddRow}
          className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.primary}`}
        >
          + Add Spread
        </button>

        {/* Only offered once there is something to add to — appending to an
            empty table is just a run. */}
        {canAppend && (
          <button
            type="button"
            onClick={() => submit(onRunAppend)}
            disabled={!isFormValid || loading}
            className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.primary} ${!isFormValid ? actionButtonTemplate.cssClasses.disabled : ''}`}
            title="Add these to the results already in the table instead of replacing them"
          >
            Add to Results
          </button>
        )}
      </div>
    </div>
  );
}

export default SpreadContractForm;
